import { ChevronRight, Sparkles } from "lucide-react";

import { AvatarPlaceholder } from "components/AvatarPlaceholder";
import { EmptyState } from "components/states";
import { Badge } from "components/ui/badge";

import type { SignalSummary } from "./directory-filters";
import type { DirectoryAttendee } from "./schemas";

const PREVIEW = 3;

/**
 * Every signal of the event with how many people picked it and who. A row
 * opens the people list filtered to that signal, which is where the full list
 * of names lives; the row itself previews the first few.
 */
export function SignalDirectory({
  summaries,
  mySignalIds,
  onSelect,
}: {
  summaries: SignalSummary[];
  mySignalIds: ReadonlySet<string>;
  onSelect: (signalId: string) => void;
}) {
  if (summaries.length === 0) {
    return (
      <EmptyState icon={Sparkles} title="No signals for this event">
        The organiser has not set up signals.
      </EmptyState>
    );
  }
  return (
    <ul className="space-y-2" data-testid="signal-directory">
      {summaries.map(({ signal, attendees }) => {
        const count = attendees.length;
        const mine = mySignalIds.has(signal.id);
        return (
          <li key={signal.id}>
            <button
              type="button"
              onClick={() => onSelect(signal.id)}
              disabled={count === 0}
              className="flex min-h-11 w-full min-w-0 items-center gap-3 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default disabled:hover:bg-card"
              data-testid={`signal-row-${signal.id}`}
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <p className="min-w-0 font-medium text-foreground [overflow-wrap:anywhere]">
                    {signal.label}
                  </p>
                  {mine && (
                    <Badge variant="outline" className="rounded-full">
                      Your pick
                    </Badge>
                  )}
                </div>
                <p
                  className="text-sm text-muted-foreground"
                  data-testid={`signal-count-${signal.id}`}
                >
                  {count === 0
                    ? "Nobody yet"
                    : `${count} ${count === 1 ? "person" : "people"}`}
                </p>
                {count > 0 && <Preview attendees={attendees} />}
              </div>
              {count > 0 && (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Preview({ attendees }: { attendees: DirectoryAttendee[] }) {
  const shown = attendees.slice(0, PREVIEW);
  const rest = attendees.length - shown.length;
  const names = shown.map((attendee) => attendee.name).join(", ");
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="flex shrink-0 -space-x-1" aria-hidden="true">
        {shown.map((attendee) =>
          attendee.avatar ? (
            <img
              key={attendee.id}
              src={attendee.avatar}
              alt=""
              className="h-7 w-7 rounded-full object-cover ring-2 ring-card"
            />
          ) : (
            <AvatarPlaceholder
              key={attendee.id}
              name={attendee.name}
              size="sm"
              className="h-7 w-7 rounded-full text-2xs ring-2 ring-card"
            />
          ),
        )}
      </div>
      <p className="min-w-0 truncate text-xs text-muted-foreground">
        {rest > 0 ? `${names} and ${rest} more` : names}
      </p>
    </div>
  );
}
